import { createSearchParams } from './search-params';

export type Url = {
  protocol: string | null;
  slashes: boolean | null;
  auth: string | null;
  host: string | null;
  port: string | null;
  hostname: string | null;
  hash: string | null;
  search: string | null;
  query: string | null;
  pathname: string | null;
  path: string | null;
  href: string;
};

// Exclude filenames
const fileName = /\/?[\w\d]+\.\w+$/;
// These paths are always external, even if they are under our domain. `/saml` paths are where we host Nemlogin
const externalPaths = /^\/(saml|api|static)/;

function splitHash(value: string) {
  const index = value.indexOf('#');
  if (index < 0) return { beforeHash: value, hash: '' };
  return {
    beforeHash: value.slice(0, index),
    hash: value.slice(index),
  };
}

function splitSearch(value: string) {
  const index = value.indexOf('?');
  if (index < 0) return { pathname: value || '/', search: '' };
  return {
    pathname: value.slice(0, index) || '/',
    search: value.slice(index),
  };
}

function parseUrl(url: string): Url {
  const value = url || '/';
  const absoluteMatch = value.match(/^([a-z][a-z\d+\-.]*:)\/\/([^/?#]*)(.*)$/i);
  const isAbsolute = !!absoluteMatch;
  const rest = isAbsolute ? absoluteMatch?.[3] || '/' : value;
  const { beforeHash, hash } = splitHash(rest || '/');
  const { pathname, search } = splitSearch(beforeHash);
  const host = absoluteMatch?.[2] || '';
  const hostname = host.split(':')[0] || '';
  const port = host.includes(':') ? host.split(':').slice(1).join(':') : '';

  return {
    protocol: isAbsolute ? absoluteMatch?.[1] || null : null,
    slashes: isAbsolute ? true : null,
    auth: null,
    host: isAbsolute ? host : null,
    port: isAbsolute ? port || null : null,
    hostname: isAbsolute ? hostname : null,
    hash: hash || null,
    search: search || null,
    query: search ? search.slice(1) : null,
    pathname,
    path: pathname + search,
    href: isAbsolute
      ? `${absoluteMatch?.[1]}//${host}${pathname}${search}${hash}`
      : pathname + search + hash,
  };
}

export function isLocalUrl(url: Url | string, target?: string): boolean {
  const parsed = typeof url === 'string' ? parseUrl(url) : url;
  if (parsed.pathname) {
    if (fileName.test(parsed.pathname)) return false;
    if (externalPaths.test(parsed.pathname)) return false;
  }

  return !parsed.protocol && !parsed.host && (!target || target === '_self');
}

export function generateRedirectUrl(
  url: string,
  redirectUrl?: string,
  cancelRedirectUrl?: string,
) {
  if (!url) {
    throw new Error('Tried to generated a redirect URL using a missing URL');
  }
  const fallbackRedirectUrl =
    typeof document !== 'undefined' ? document.location.href : '/';
  const { pathname = '/', search } = parseUrl(
    redirectUrl || fallbackRedirectUrl,
  );

  const returnUrl = pathname + (search || '');

  if (url.includes('##redirect##')) {
    // If the URL contains a "##redirect"" variable, just replace that and return the url.
    return url.replace('##redirect##', encodeURIComponent(returnUrl));
  }

  const parsed = parseUrl(url);
  const params = createSearchParams(parsed.search || '');
  params.set('ReturnUrl', returnUrl);
  parsed.search = params.toString();

  if (cancelRedirectUrl) {
    parsed.search += `&cancelOverrideReturnUrl=${encodeURIComponent(
      cancelRedirectUrl,
    )}`;
  }

  return (
    (parsed.host ? `${parsed.protocol}//${parsed.host}` : '') +
    `${parsed.pathname}?${parsed.search}`
  );
}

export function normalizePathname(
  pathname: string | null,
  addTrailingSlash: boolean = false,
) {
  if (!pathname) return pathname;
  if (addTrailingSlash) {
    if (!pathname.endsWith('/')) {
      return pathname + '/';
    }
  } else if (pathname !== '/' && pathname.endsWith('/')) {
    return pathname.substr(0, pathname.length - 1);
  }

  return pathname;
}

export function normalizeUrl(
  url: string = '/',
  addTrailingSlash: boolean = false,
): Url {
  const parsed = parseUrl(url);

  parsed.pathname = normalizePathname(parsed.pathname, addTrailingSlash);
  // Remove standalone '#' - Leftover from Umbraco
  if (parsed.hash === '#') parsed.hash = '';

  return parsed;
}

export function formatUrl(url: Url): string {
  return (
    (url.host ? `${url.protocol || ''}//${url.host}` : '') +
    `${url.pathname || ''}${url.search || ''}${url.hash || ''}`
  );
}
